
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

// Function to migrate existing users to user_roles table
export const migrateExistingUsers = async (): Promise<{ success: boolean; message: string }> => {
  try {
    // First check if we have service role access
    let hasServiceAccess = false;
    try {
      const { data: testData, error: testError } = await supabase.auth.admin.listUsers({ page: 1, perPage: 1 });
      if (!testError && testData) {
        hasServiceAccess = true;
      }
    } catch (error) {
      console.log("No service role access available for migration");
    }

    if (hasServiceAccess) {
      // Get existing users from Auth
      const { data: authUsers, error: authError } = await supabase.auth.admin.listUsers();
      
      if (authError) {
        console.error("Error fetching users from auth:", authError);
        return { success: false, message: `שגיאה בטעינת משתמשים מאימות: ${authError.message}` };
      }

      if (!authUsers || !authUsers.users || authUsers.users.length === 0) {
        return { success: false, message: "לא נמצאו משתמשים במערכת האימות" };
      }

      // Get existing user_roles for comparison
      const { data: existingRoles, error: rolesError } = await supabase
        .from("user_roles")
        .select("user_id, role");

      if (rolesError) {
        console.error("Error fetching existing roles:", rolesError);
        return { success: false, message: `שגיאה בטעינת תפקידים קיימים: ${rolesError.message}` };
      }

      // Create a map of existing roles
      const existingRolesMap = new Map();
      if (existingRoles) {
        existingRoles.forEach(role => {
          existingRolesMap.set(role.user_id, role.role);
        });
      }

      // Prepare data for batch insert
      const rolesToInsert = [];

      for (const user of authUsers.users) {
        // Skip if the user already has a role
        if (!existingRolesMap.has(user.id)) {
          rolesToInsert.push({
            user_id: user.id,
            role: "member", // Default role for migrated users
            is_active: true,
            plan_tier: "basic",
            subscription_status: "free"
          });
        }
      }

      if (rolesToInsert.length === 0) {
        return { success: true, message: "כל המשתמשים כבר קיימים במערכת הרשאות - אין צורך במיגרציה" };
      }

      // Batch insert new roles
      const { error: insertError } = await supabase
        .from("user_roles")
        .insert(rolesToInsert);

      if (insertError) {
        console.error("Error inserting roles:", insertError);
        return { success: false, message: `שגיאה בהוספת תפקידים: ${insertError.message}` };
      }

      return { 
        success: true, 
        message: `${rolesToInsert.length} משתמשים הועברו בהצלחה למערכת ניהול תפקידים` 
      };
    } else {
      // Fallback for development - try to get users from user_id columns in other tables
      const { data: settingsData, error: settingsError } = await supabase
        .from('user_settings')
        .select('user_id');
      
      // Fix: Remove .distinct() as it's not available in this context
      
      if (settingsError) {
        console.error("Error fetching user IDs from settings:", settingsError);
        return { 
          success: false, 
          message: "אין גישת שירות וגם לא ניתן לשלוף נתוני משתמשים מטבלאות אחרות" 
        };
      }
      
      // Get existing user_roles to avoid duplicates
      const { data: existingRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id');
      
      if (rolesError) {
        console.error("Error fetching existing roles:", rolesError);
        return { success: false, message: `שגיאה בטעינת תפקידים קיימים: ${rolesError.message}` };
      }
      
      // Create a set of existing user IDs in roles
      const existingUserIds = new Set();
      if (existingRoles) {
        existingRoles.forEach(role => existingUserIds.add(role.user_id));
      }
      
      // Filter out users that already have roles and deduplicate user IDs
      const uniqueUserIds = new Set();
      const usersToMigrate = [];
      
      if (settingsData) {
        for (const item of settingsData) {
          // Only add users that don't already have roles and haven't been processed yet
          if (!existingUserIds.has(item.user_id) && !uniqueUserIds.has(item.user_id)) {
            uniqueUserIds.add(item.user_id);
            usersToMigrate.push({
              user_id: item.user_id,
              role: "member",
              is_active: true,
              plan_tier: "basic",
              subscription_status: "free"
            });
          }
        }
      }
      
      if (usersToMigrate.length === 0) {
        return { 
          success: true, 
          message: "לא נמצאו משתמשים חדשים להעברה או שכולם כבר קיימים במערכת הרשאות" 
        };
      }
      
      // Insert new roles
      const { error: insertError } = await supabase
        .from('user_roles')
        .insert(usersToMigrate);
      
      if (insertError) {
        console.error("Error inserting roles:", insertError);
        return { success: false, message: `שגיאה בהוספת תפקידים: ${insertError.message}` };
      }
      
      return { 
        success: true, 
        message: `${usersToMigrate.length} משתמשים הועברו בהצלחה למערכת ניהול תפקידים (מצב פיתוח)` 
      };
    }
  } catch (error: any) {
    console.error("Migration error:", error);
    return { success: false, message: `שגיאה במיגרציה: ${error.message || 'שגיאה לא ידועה'}` };
  }
};

// Function to promote a user to super admin
export const promoteToSuperAdmin = async (userId: string): Promise<{ success: boolean; message: string }> => {
  try {
    // First check if the user exists in user_roles
    const { data: existingRole, error: checkError } = await supabase
      .from("user_roles")
      .select("*")
      .eq("user_id", userId)
      .single();

    if (checkError && checkError.code !== "PGRST116") { // PGRST116 is the "no rows returned" error
      console.error("Error checking for existing role:", checkError);
      return { success: false, message: `שגיאה בבדיקת תפקיד קיים: ${checkError.message}` };
    }

    if (existingRole) {
      // Update existing role
      const { error: updateError } = await supabase
        .from("user_roles")
        .update({ role: "super" })
        .eq("user_id", userId);

      if (updateError) {
        console.error("Error updating role:", updateError);
        return { success: false, message: `שגיאה בעדכון תפקיד: ${updateError.message}` };
      }
    } else {
      // Insert new role
      const { error: insertError } = await supabase
        .from("user_roles")
        .insert({
          user_id: userId,
          role: "super",
          is_active: true,
          plan_tier: "basic",
          subscription_status: "free"
        });

      if (insertError) {
        console.error("Error inserting role:", insertError);
        return { success: false, message: `שגיאה בהוספת תפקיד: ${insertError.message}` };
      }
    }

    return { success: true, message: "המשתמש קודם בהצלחה לדרגת מנהל מערכת" };
  } catch (error: any) {
    console.error("Error promoting to super admin:", error);
    return { success: false, message: `שגיאה בקידום לדרגת מנהל מערכת: ${error.message || 'שגיאה לא ידועה'}` };
  }
};
